/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * gridConfig.js
 * @description Grid column definitions and filter parameters for the FuzzyVRM grid.
 * Defines all columns displayed in the AG-Grid, including headers, filters, and renderers.
 * The column definitions are generated dynamically based on the onViewImageClick callback.
 */
import React from 'react'
import {
  customDateRenderer,
  customTextRenderer,
} from '../../../../../components/base/grid/CustomRenderer'
import utils from '../../../../../../utils/utils'

/**
 * Common filter parameters used across date and text columns
 * @type {Object}
 * @property {Array<string>} filterOptions - Available filter types (e.g., 'Equals')
 * @property {boolean} suppressAndOrCondition - Disables AND/OR condition in filter
 * @property {boolean} browserDatePicker - Use browser's native date picker
 */
export const GRID_FILTER_PARAMS = {
  filterOptions: ['Equals'],
  suppressAndOrCondition: true,
  browserDatePicker: true,
}

/**
 * Generates the array of column definitions for the FuzzyVRM grid
 * Includes columns for reference number, VRM, entry/exit dates and times, status, and actions.
 *
 * @function
 * @param {Function} onViewImageClick - Callback function for the view image action button
 * @returns {Array<Object>} Array of AG-Grid column definition objects
 *
 * @example
 * const columns = getGridColumns(handleViewImage)
 * columnsInclude:
 * - VRM (text with filter)
 * - Entry Date (date with filter)
 * - Entry Time (time display)
 * - Exit Date (date with filter)
 * - Exit Time (time display)
 * - Status (text with filter)
 * - Action (view image button)
 */

/* istanbul ignore next */
export const getGridColumns = (onViewImageClick) => [
  {
    headerName: 'VRM',
    field: 'vrmNo',
    filter: false,
    sortable: false,
    cellRenderer: customTextRenderer,
  },
  {
    headerName: 'Entry Date',
    field: 'transitStartTime',
    filter: false,
    sortable: false,
    valueGetter: utils?.convertUtcDateTimeToFormattedDate,
    cellRenderer: (params) => customDateRenderer(params, 'default'),
  },
  {
    headerName: 'Entry Time',
    field: 'transitStartTime',
    filter: false,
    sortable: false,
    valueFormatter: (params) => {
      if (!params.value) return ''
      const timeMatch = params.value.match(/T(\d{2}:\d{2}:\d{2})/)
      return timeMatch ? timeMatch[1] : ''
    },
  },
  {
    headerName: 'Exit Date',
    field: 'transitEndTime',
    filter: false,
    sortable: false,
    valueGetter: utils?.convertUtcDateTimeToFormattedDate,
    cellRenderer: (params) => customDateRenderer(params, 'default'),
  },
  {
    headerName: 'Exit Time',
    field: 'transitEndTime',
    filter: false,
    sortable: false,
    valueFormatter: (params) => {
      if (!params.value) return ''
      const timeMatch = params.value.match(/T(\d{2}:\d{2}:\d{2})/)
      return timeMatch ? timeMatch[1] : ''
    },
  },
  {
    headerName: 'Status',
    field: 'taskStatus',
    filter: false,
    sortable: false,
    cellRenderer: customTextRenderer,
  },
  {
    headerName: '',
    field: 'action',
    filter: false,
    sortable: false,
    cellRenderer: (params) => {
      const hasValidAttachment =
        Array.isArray(params?.data?.attachments) &&
        params.data.attachments.some((attachment) =>
          Boolean(attachment?.fileLocation)
        )

      if (!hasValidAttachment) return null

      return (
        <button
          className="generateBatchBtn"
          onClick={() => onViewImageClick(params)}
        >
          {'View Image'}
        </button>
      )
    },
  },
]
